import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import CountUp from "react-countup";
import { FileText, Users, CheckCircle, Clock, XCircle, TrendingUp, PlusCircle, Calendar } from "react-feather";
import DashboardServices from "../../services/Dashboard";
import { ErrorToaster } from "../components/toaster/Toaster";
import { all_routes } from "../../Router/all_routes";
import { getMonthDate } from "../../utils";
import dayjs from "dayjs";
import FeatureHighlights from "../components/FeatureHighlights";
import ComponentLoader from "../components/ComponentLoader";
import "bootstrap-daterangepicker/daterangepicker.css";
import DateRangePicker from "react-bootstrap-daterangepicker";
import ImageWithBasePath from "../../core/img/imagewithbasebath";

const Dashboard = () => {

  const routes = all_routes;
  const { userData } = useSelector((state) => ({ userData: state.user_detail }));
  const isAdmin = userData?.role === 'admin';
  const [showFeatures, setShowFeatures] = useState(
    () => sessionStorage.getItem('features_dismissed') !== '1'
  );
  const defaultRange = [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')];
  const [dateRange, setDateRange] = useState(defaultRange);
  const [loading, setLoading] = useState(false);
  const [topUsersLoading, setTopUsersLoading] = useState(false);
  const [stats, setStats] = useState({});
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [topUsersPeriod, setTopUsersPeriod] = useState('monthly');

  const getDashboard = async (from, to) => {
    const f = from || dateRange[0].format('YYYY-MM-DD');
    const t = to || dateRange[1].format('YYYY-MM-DD');
    setLoading(true);
    try {
      const { data } = isAdmin
        ? await DashboardServices.getAdminDashboard(topUsersPeriod, f, t)
        : await DashboardServices.getUserDashboard(f, t);

      setStats(data?.stats || {});
      setMonthlyTrend(data?.monthly_trend || []);
      setRecentInvoices(data?.recent_invoices || []);
      setRecentUsers(data?.recent_users || []);
      setTopUsers(data?.top_users || []);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTopUsersPeriod = async (period) => {
    setTopUsersPeriod(period);
    setTopUsersLoading(true);
    try {
      const { data } = await DashboardServices.getAdminDashboard(period);
      setTopUsers(data?.top_users || []);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setTopUsersLoading(false);
    }
  };

  useEffect(() => {
    getDashboard();
  }, []);

  // Build last 6 month labels to fill gaps
  const buildMonthlyData = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      months.push(dayjs().subtract(i, 'month').format('YYYY-MM'));
    }
    return {
      labels: months.map((m) => dayjs(m).format('MMM YYYY')),
      data: months.map((m) => {
        const found = monthlyTrend.find((t) => t.month === m);
        return found ? parseInt(found.count) : 0;
      }),
    };
  };

  const { labels: trendLabels, data: trendData } = buildMonthlyData();

  const barChartOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    colors: ['#2D4F8F'],
    xaxis: { categories: trendLabels },
    yaxis: { labels: { formatter: (v) => Math.round(v) } },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f1f1f1' },
  };

  const donutLabels = ['Pending', 'Valid', 'Submitted', 'Invalid'];
  const donutData = isAdmin
    ? [
      stats?.status_breakdown?.PENDING || 0,
      stats?.status_breakdown?.VALID || 0,
      stats?.status_breakdown?.SUBMITTED || 0,
      stats?.status_breakdown?.INVALID || 0,
    ]
    : [stats?.PENDING || 0, stats?.VALID || 0, stats?.SUBMITTED || 0, stats?.INVALID || 0];

  const donutOptions = {
    chart: { type: 'donut' },
    labels: donutLabels,
    colors: ['#FFC107', '#17A2B8', '#28A745', '#DC3545'],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
  };

  const topUsersBarOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, horizontal: true } },
    colors: ['#2D4F8F'],
    xaxis: {
      categories: topUsers.map((u) => u['user.name'] || u?.user?.name || 'Unknown'),
      labels: { formatter: (v) => Math.round(v) },
    },
    dataLabels: { enabled: false },
    grid: { borderColor: '#f1f1f1' },
  };

  const statusBadge = (status) => {
    const map = {
      PENDING: 'badge-linegray',
      VALID: 'badge-linewarning',
      SUBMITTED: 'badge-linesuccess',
      INVALID: 'badge-linedanger',
    };
    return <span className={`badge ${map[status] || 'badge-linegray'} text-capitalize`}>{status}</span>;
  };

  // ── User stat cards ──
  const userStatCards = [
    { label: 'Total Invoices', value: stats?.total || 0, icon: <FileText size={24} />, color: '#2D4F8F', bg: '#EEF2FF' },
    { label: 'Submitted', value: stats?.SUBMITTED || 0, icon: <CheckCircle size={24} />, color: '#28A745', bg: '#EAFAF1' },
    { label: 'Pending', value: stats?.PENDING || 0, icon: <Clock size={24} />, color: '#FFC107', bg: '#FFFBEA' },
    { label: 'Invalid', value: stats?.INVALID || 0, icon: <XCircle size={24} />, color: '#DC3545', bg: '#FEECEC' },
  ];

  // ── Admin stat cards ──
  const adminStatCards = [
    { label: 'Total Users', value: stats?.total_users || 0, icon: <Users size={24} />, color: '#2D4F8F', bg: '#EEF2FF' },
    { label: 'Active Users', value: stats?.active_users || 0, icon: <CheckCircle size={24} />, color: '#28A745', bg: '#EAFAF1' },
    { label: 'Total Invoices', value: stats?.total_invoices || 0, icon: <FileText size={24} />, color: '#17A2B8', bg: '#E8F8FA' },
    { label: 'Submitted Invoices', value: stats?.status_breakdown?.SUBMITTED || 0, icon: <TrendingUp size={24} />, color: '#6F42C1', bg: '#F3EEFF' },
  ];

  const statCards = isAdmin ? adminStatCards : userStatCards;

  const initialSettings = {
    autoUpdateInput: true,
    opens: "left",
    startDate: dayjs().subtract(6, "day").toDate(),
    endDate: dayjs().toDate(),
    ranges: {
      Today: [dayjs().toDate(), dayjs().toDate()],
      Yesterday: [
        dayjs().subtract(1, "day").toDate(),
        dayjs().subtract(1, "day").toDate(),
      ],
      "Last 7 Days": [
        dayjs().subtract(6, "day").toDate(),
        dayjs().toDate(),
      ],
      "Last 30 Days": [
        dayjs().subtract(29, "day").toDate(),
        dayjs().toDate(),
      ],
      "This Month": [
        dayjs().startOf("month").toDate(),
        dayjs().endOf("month").toDate(),
      ],
      "Last Month": [
        dayjs().subtract(1, "month").startOf("month").toDate(),
        dayjs().subtract(1, "month").endOf("month").toDate(),
      ],
    },
  };

  return (
    <Fragment>
      <div className="page-wrapper">
        <div className="content">

          {/* Page Header */}
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Dashboard</h4>
                <h6>Welcome back, {userData?.name}</h6>
              </div>
            </div>
            {!isAdmin && (
              <div className="page-btn">
                <Link to={routes.addInvoice} className="btn btn-added">
                  <PlusCircle className="me-2" size={16} />
                  Create Invoice
                </Link>
              </div>
            )}
          </div>

          {/* Feature Highlights */}
          {showFeatures && (
            <div className="row mb-4">
              <div className="col-12">
                <FeatureHighlights
                  onDismiss={() => {
                    sessionStorage.setItem('features_dismissed', '1');
                    setShowFeatures(false);
                  }}
                />
              </div>
            </div>
          )}

          <div className="welcome d-lg-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center welcome-text">
              <h3 className="d-flex align-items-center">
                <ImageWithBasePath src="assets/img/icons/hi.svg" alt="img" />
                &nbsp;Hi {userData?.name},
              </h3>
              &nbsp;
              <h6>here&apos;s what&apos;s happening with your portal today.</h6>
            </div>
            <div className="d-flex align-items-center">
              <div className="position-relative daterange-wraper me-2">
                <div className="input-groupicon calender-input">
                  <DateRangePicker
                    initialSettings={initialSettings}
                    onApply={(event, picker) => {
                      const from = dayjs(picker.startDate).format("YYYY-MM-DD");
                      const to = dayjs(picker.endDate).format("YYYY-MM-DD");
                      setDateRange([dayjs(picker.startDate), dayjs(picker.endDate)]);
                      getDashboard(from, to);
                    }}
                  >
                    <input
                      className="form-control col-4 input-range"
                      type="text"
                    // style={{ border: "none" }}
                    />
                  </DateRangePicker>
                </div>
                <Calendar className="feather-14" />
              </div>
            </div>
          </div>
          {loading ? (
            <ComponentLoader minHeight="400px" />
          ) : (
            <>
              {/* Stat Cards */}
              <div className="row mb-4">
                {statCards.map((card, i) => (
                  <div className="col-lg-3 col-sm-6 col-12 mb-3" key={i}>
                    <div className="card h-100 mb-0" style={{ border: '1px solid #e9ecef' }}>
                      <div className="card-body d-flex align-items-center gap-3 py-3">
                        <div
                          style={{
                            width: 52, height: 52, borderRadius: 12,
                            background: card.bg, color: card.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          }}
                        >
                          {card.icon}
                        </div>
                        <div>
                          <p className="mb-1 text-muted" style={{ fontSize: 13 }}>{card.label}</p>
                          <h4 className="mb-0 fw-bold">
                            <CountUp end={card.value} duration={1.2} />
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

              </div>

              {/* Charts Row */}
              <div className="row mb-4">
                <div className={isAdmin ? "col-lg-8 col-12 mb-3" : "col-lg-8 col-12 mb-3"}>
                  <div className="card mb-0 h-100" style={{ border: '1px solid #e9ecef' }}>
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Monthly Invoice Trend</h6>
                      <ReactApexChart
                        type="bar"
                        options={barChartOptions}
                        series={[{ name: 'Invoices', data: trendData }]}
                        height={240}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-4 col-12 mb-3">
                  <div className="card mb-0 h-100" style={{ border: '1px solid #e9ecef' }}>
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Invoice Status Breakdown</h6>
                      {donutData.every((v) => v === 0) ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ height: 240 }}>
                          <p className="text-muted">No data yet</p>
                        </div>
                      ) : (
                        <ReactApexChart
                          type="donut"
                          options={donutOptions}
                          series={donutData}
                          height={240}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Admin: Top Users chart */}
              {isAdmin && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card mb-0" style={{ border: '1px solid #e9ecef' }}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold mb-0">Top 5 Users by Invoice Count</h6>
                          <div className="btn-group">
                            {['weekly', 'monthly', 'quarterly'].map((p) => (
                              <button
                                key={p}
                                type="button"
                                className={`btn btn-sm ${topUsersPeriod === p ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => handleTopUsersPeriod(p)}
                                disabled={topUsersLoading}
                                style={{ textTransform: 'capitalize' }}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>
                        {topUsersLoading ? (
                          <ComponentLoader minHeight="220px" />
                        ) : topUsers.length === 0 ? (
                          <div className="d-flex align-items-center justify-content-center" style={{ height: 220 }}>
                            <p className="text-muted">No data for selected period</p>
                          </div>
                        ) : (
                          <ReactApexChart
                            type="bar"
                            options={topUsersBarOptions}
                            series={[{ name: 'Invoices', data: topUsers.map((u) => parseInt(u.dataValues?.invoice_count || u.invoice_count || 0)) }]}
                            height={220}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bottom Tables Row */}
              <div className="row">
                {/* Recent Invoices */}
                <div className={isAdmin ? "col-lg-7 col-12 mb-3" : "col-12 mb-3"}>
                  <div className="card mb-0" style={{ border: '1px solid #e9ecef' }}>
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold mb-0">Recent Invoices</h6>
                        <Link to={routes.invoices} className="btn btn-sm btn-outline-primary">View All</Link>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead>
                            <tr>
                              {isAdmin && <th>User</th>}
                              <th>Invoice Ref</th>
                              <th>Buyer</th>
                              <th>NTN/CNIC</th>
                              <th>Date</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentInvoices.length === 0 ? (
                              <tr><td colSpan={isAdmin ? 6 : 5} className="text-center text-muted py-3">No invoices yet</td></tr>
                            ) : (
                              recentInvoices.map((inv, i) => (
                                <tr key={i}>
                                  {isAdmin && <td style={{ fontSize: 13 }}>{inv?.user?.name || '—'}</td>}
                                  <td style={{ fontSize: 13 }}>{inv.invoice_ref_no || '—'}</td>
                                  <td style={{ fontSize: 13 }}>{inv.buyer_business_name || '—'}</td>
                                  <td style={{ fontSize: 13 }}>{inv.buyer_ntn_cnic || '—'}</td>
                                  <td style={{ fontSize: 13 }}>{getMonthDate(inv.invoice_date)}</td>
                                  <td>{statusBadge(inv.status)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin: Recent Users */}
                {isAdmin && (
                  <div className="col-lg-5 col-12 mb-3">
                    <div className="card mb-0" style={{ border: '1px solid #e9ecef' }}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="fw-bold mb-0">Recent Users</h6>
                          <Link to={routes.users} className="btn btn-sm btn-outline-primary">View All</Link>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {recentUsers.length === 0 ? (
                                <tr><td colSpan={3} className="text-center text-muted py-3">No users yet</td></tr>
                              ) : (
                                recentUsers.map((user, i) => (
                                  <tr key={i}>
                                    <td style={{ fontSize: 13 }}>{user.name}</td>
                                    <td style={{ fontSize: 13 }}>{user.email}</td>
                                    <td>
                                      <span className={`badge ${user.status === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                                        {user.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </Fragment>
  );
};

export default Dashboard;
